export const SUPPORTED_LOCALES = ['en', 'fr', 'hu', 'ru', 'de'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_STORAGE_KEY = 'vivaassess_locale';

export const LOCALE_TO_BCP47: Record<SupportedLocale, string> = {
  en: 'en-US',
  fr: 'fr-FR',
  hu: 'hu-HU',
  ru: 'ru-RU',
  de: 'de-DE'
};

export const LOCALE_TO_LANGUAGE_LABEL_KEY: Record<SupportedLocale, string> = {
  en: 'language.english',
  fr: 'language.french',
  hu: 'language.hungarian',
  ru: 'language.russian',
  de: 'language.german'
};

export function isSupportedLocale(value: string): value is SupportedLocale {
  return SUPPORTED_LOCALES.includes(value as SupportedLocale);
}

function readLocaleToken(value: string | null | undefined): string {
  if (!value) return '';

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return '';

  const [token] = normalized.split(',');
  const [languageTag] = token.split(';');
  const [primary] = languageTag.split(/[-_]/);
  return primary;
}

export function normalizeLocaleCode(value: string | null | undefined): SupportedLocale {
  const token = readLocaleToken(value);
  return isSupportedLocale(token) ? token : 'en';
}

export function parseAcceptLanguageHeader(headerValue: string | null | undefined): SupportedLocale {
  if (!headerValue) return 'en';

  const candidates = String(headerValue)
    .split(',')
    .map((entry) => {
      const [rawTag, ...params] = entry.trim().split(';');
      const qualityPart = params.find((part) => part.trim().startsWith('q='));
      const quality = qualityPart ? Number.parseFloat(qualityPart.split('=')[1]) : 1;
      const localeToken = readLocaleToken(rawTag);

      return {
        locale: isSupportedLocale(localeToken) ? localeToken : null,
        quality: Number.isFinite(quality) ? quality : 0
      };
    })
    .filter((entry): entry is { locale: SupportedLocale; quality: number } => Boolean(entry.locale) && entry.quality > 0)
    .sort((left, right) => right.quality - left.quality);

  if (candidates.length === 0) {
    return 'en';
  }

  return candidates[0].locale;
}

export function toBcp47Locale(value: string | null | undefined): string {
  return LOCALE_TO_BCP47[normalizeLocaleCode(value)];
}

export function getStoredLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') return null;
  const storage = window.localStorage as Partial<Storage> | undefined;
  if (!storage || typeof storage.getItem !== 'function') {
    return null;
  }

  const stored = storage.getItem(LOCALE_STORAGE_KEY);
  if (!stored) return null;

  const normalized = normalizeLocaleCode(stored);
  if (!isSupportedLocale(normalized)) return null;
  return normalized;
}

export function detectBrowserLocale(): SupportedLocale {
  if (typeof navigator === 'undefined') return 'en';

  const preferred = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language
  ];

  for (const language of preferred) {
    const normalized = normalizeLocaleCode(language);
    if (isSupportedLocale(normalized)) {
      return normalized;
    }
  }

  return 'en';
}

export function persistLocale(value: string | null | undefined): SupportedLocale {
  const normalized = normalizeLocaleCode(value);

  if (typeof window !== 'undefined') {
    const storage = window.localStorage as Partial<Storage> | undefined;
    if (storage && typeof storage.setItem === 'function') {
      storage.setItem(LOCALE_STORAGE_KEY, normalized);
    }
  }

  return normalized;
}

export function getEffectiveLocale(preferred?: string | null): SupportedLocale {
  if (preferred) {
    return normalizeLocaleCode(preferred);
  }

  return getStoredLocale() || detectBrowserLocale();
}

export function getEffectiveBcp47Locale(preferred?: string | null): string {
  return toBcp47Locale(getEffectiveLocale(preferred));
}

/** Interview language codes that may appear on interview.language (admin form). */
const INTERVIEW_LANGUAGE_TO_BCP47: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  hu: 'hu-HU',
  ru: 'ru-RU'
};

/**
 * Resolve BCP-47 for TTS/STT from the interview language first, then UI locale.
 * Ensures speech recognition and synthesis follow the assessment language.
 */
export function resolveSpeechBcp47(interviewLanguage?: string | null): string {
  const token = readLocaleToken(interviewLanguage);
  if (token && INTERVIEW_LANGUAGE_TO_BCP47[token]) {
    return INTERVIEW_LANGUAGE_TO_BCP47[token];
  }
  return getEffectiveBcp47Locale();
}

/**
 * Resolve content locale for localized greeting/closing templates.
 * Prefers interview.language when we have UI templates; otherwise UI locale.
 * Note: Spanish viva speech templates live in interviewSpeech (SpeechContentLocale);
 * callers that need es speech should use resolveSpeechLocale / resolveSpeechBcp47.
 */
export function resolveSpeechContentLocale(interviewLanguage?: string | null): SupportedLocale {
  const token = readLocaleToken(interviewLanguage);
  if (token && isSupportedLocale(token)) {
    return token;
  }
  // Spanish interviews: UI i18n has no `es` pack — map content helpers to English
  // while STT/TTS still use es-ES via resolveSpeechBcp47 / interviewSpeech es templates.
  if (token === 'es') {
    return 'en';
  }
  return getEffectiveLocale();
}

export function setDocumentLanguageTag(locale: string | null | undefined): void {
  if (typeof document === 'undefined') return;

  document.documentElement.lang = toBcp47Locale(locale);
}

export function localeCompare(
  left: string,
  right: string,
  options?: Intl.CollatorOptions,
  locale?: string | null
): number {
  const targetLocale = toBcp47Locale(locale || getStoredLocale() || detectBrowserLocale());
  return new Intl.Collator(targetLocale, options).compare(left, right);
}
